import React, { useRef, useState } from 'react';

import dayjs from '~/timeInit';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useApproveHeatmapSurcharge } from '~/hooks/apis/lere/useApproveHeatmapSurcharge';

import { HeatmapSurchargeApproval } from '~/types/heatmapSurcharge';

import currencyFormatter from '~/utils/currencyFormatter';

interface Props {
  approval: HeatmapSurchargeApproval;
  roomTypeName: string;
  onClose: () => void;
  onQueued: (jobId: string) => void;
}

export default function HeatmapApprovalDialog({ approval, roomTypeName, onClose, onQueued }: Props) {
  const [note, setNote] = useState('');
  const mutation = useApproveHeatmapSurcharge();
  const submitting = useRef(false);
  const quote = approval.quote;

  async function confirm() {
    if (submitting.current) return;
    submitting.current = true;
    try {
      const result = await mutation.mutateAsync({ ...approval, note: note.trim() });
      onQueued(result.jobId);
    } catch {
      // The mutation error stays in the dialog; never retry a price action automatically.
    } finally {
      submitting.current = false;
    }
  }

  return (
    <Dialog
      open
      onClose={mutation.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="heatmap-approval-title"
    >
      <DialogTitle id="heatmap-approval-title">Confirm heatmap surcharge</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography fontWeight="bold">
            {roomTypeName} · {dayjs(quote.travelDate).format('D MMM YYYY')}
          </Typography>
          <Typography variant="body2">Offer: {approval.offerId}</Typography>
          <Typography>
            Set the total internal surcharge to{' '}
            <strong>{currencyFormatter('AUD', quote.targetInternalSurcharge, 2)}</strong> (
            {quote.suggestedSurchargePercentage}% of the reference base price).
          </Typography>
          <Typography>
            Additional surcharge: <strong>{currencyFormatter('AUD', quote.additionalInternalSurcharge, 2)}</strong>.
            Reference nightly price: {currencyFormatter('AUD', quote.currentNightlyPrice, 2)} →{' '}
            <strong>{currencyFormatter('AUD', quote.proposedNightlyPrice, 2)}</strong>.
          </Typography>
          <Alert severity="info">
            This applies to eligible rate plans for this room type and date. Existing room-price spacing rules may also
            adjust higher room types. The change is recorded under the Heatmap channel.
          </Alert>
          <TextField
            label="Note (optional)"
            multiline
            minRows={2}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            inputProps={{ maxLength: 1000 }}
            helperText={`${note.length}/1000`}
            disabled={mutation.isPending}
          />
          {mutation.isError && (
            <Alert severity="error">
              {mutation.error.message} If the submission’s outcome is unclear, check the Hotels Change Log before
              retrying.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" disabled={mutation.isPending} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={mutation.isPending || mutation.isSuccess || mutation.isError}
          onClick={confirm}
        >
          {mutation.isPending ? 'Submitting…' : 'Confirm and apply surcharge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
